import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { USER_addres } from '../../API/API';

/**
 * Страница на которую Яндекс редиректит после авторизации.
 * URL: /auth/yandex/callback?code=...&state=...
 *
 * После обмена code → JWT делается access-check админки.
 * Если есть доступ — /main, иначе — /no-access.
 */
const YandexCallback = () => {
    const navigate = useNavigate();
    const { login: authLogin } = useAuth();
    const [error, setError] = useState('');
    const [step, setStep] = useState('Подключаемся к Яндексу...');
    const exchangedRef = useRef(false);

    useEffect(() => {
        if (exchangedRef.current) return;
        exchangedRef.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const yandexError = params.get('error');

        if (yandexError) {
            setError('Яндекс отменил вход: ' + yandexError);
            return;
        }
        if (!code || !state) {
            setError('Неполные данные от Яндекса');
            return;
        }

        (async () => {
            try {
                setStep('Получаем ваш профиль...');
                const r = await fetch(`${USER_addres}/api/v1/users/auth/yandex/exchange`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code, state }),
                });
                const data = await r.json();
                if (!r.ok || !data.token) {
                    setError(data.message || 'Не удалось завершить вход');
                    return;
                }
                localStorage.setItem('Authorization', data.token);
                if (data.refreshToken) {
                    localStorage.setItem('refreshToken', data.refreshToken);
                }

                // Проверим доступ к админке ВРУЧНУЮ через прямой fetch
                // (без axios-interceptor который чистит токен на 401).
                // 2026-05-31: НЕ кидаем на /no-access при 401/5xx —
                // AuthProvider сделает fallback на /users/profile по role.
                setStep('Проверяем доступ к АдминПанели...');
                let hasAccess = true;
                try {
                    const ac = await fetch('https://minecraftia-school.ru/api-admin/api/v1/admin/access-check', {
                        headers: { 'Authorization': data.token },
                    });
                    if (ac.ok) {
                        const acData = await ac.json();
                        hasAccess = Boolean(acData?.has_access);
                    }
                    // не-200 (401/403/5xx) — оставляем true, fallback в AuthProvider
                } catch (e) {
                    // access-check недоступен — пускаем дальше и пусть ProtectedRoute решит.
                    hasAccess = true;
                }

                setStep('Заходим в кабинет...');
                if (!hasAccess) {
                    // Жёсткий редирект на /no-access, токен сохраняется.
                    setTimeout(() => { window.location.replace('/no-access'); }, 200);
                } else {
                    setTimeout(() => { window.location.replace('/main'); }, 200);
                }
            } catch (e) {
                setError('Сеть недоступна, попробуйте ещё раз');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#0f1115',
            color: '#fff',
            fontFamily: "'Inter', system-ui, sans-serif",
            padding: 20,
        }}>
            <div style={{
                maxWidth: 420,
                width: '100%',
                background: '#161922',
                border: '1px solid #232735',
                borderRadius: 16,
                padding: 32,
                textAlign: 'center',
            }}>
                {error ? (
                    <>
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: '#fee2e2', color: '#dc2626',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px', fontSize: 32, fontWeight: 700,
                        }}>!</div>
                        <h2 style={{ fontSize: 20, marginBottom: 12 }}>Не удалось войти</h2>
                        <p style={{ color: '#94a3b8', marginBottom: 20 }}>{error}</p>
                        <button
                            onClick={() => navigate('/login')}
                            style={{
                                padding: '10px 20px', background: '#3b82f6',
                                color: '#fff', border: 'none', borderRadius: 8,
                                cursor: 'pointer', fontSize: 14, fontWeight: 600,
                            }}
                        >Вернуться ко входу</button>
                    </>
                ) : (
                    <>
                        <div style={{
                            width: 48, height: 48,
                            border: '4px solid #232735',
                            borderTopColor: '#FC3F1D',
                            borderRadius: '50%',
                            margin: '0 auto 16px',
                            animation: 'spin 1s linear infinite',
                        }} />
                        <h2 style={{ fontSize: 18, marginBottom: 8 }}>Вход через Яндекс</h2>
                        <p style={{ color: '#94a3b8' }}>{step}</p>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </>
                )}
            </div>
        </div>
    );
};

export default YandexCallback;
